import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../customButton/CustomButton";
import DialogComponent from "../dialogs/DialogComponent";
import RichTextMessageEditor from "../richTextMessageEditor";

type RejectedMessageEditorMode = "template" | "custom";

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  onSend: (message: string) => void;
  fetchTemplateMessage: () => Promise<string>;
}

const RejectedMessageDialog = ({
  open,
  title,
  onClose,
  onSend,
  fetchTemplateMessage,
}: Props) => {
  const { t } = useTranslation(["order", "common"]);
  const [editorMode, setEditorMode] = useState<RejectedMessageEditorMode>(
    "template",
  );
  const [isTemplateEditable, setIsTemplateEditable] = useState(false);
  const [templateRejectedMessage, setTemplateRejectedMessage] = useState("");
  const [customRejectedMessage, setCustomRejectedMessage] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }
    setEditorMode("template");
    setIsTemplateEditable(false);
    setTemplateRejectedMessage("");
    setCustomRejectedMessage("");
    void fetchTemplateMessage().then((message) => {
      setTemplateRejectedMessage(message);
    });
  }, [open, fetchTemplateMessage]);

  const hasContent = (value: string) =>
    value
      .replace(/<[^>]*>/g, "")
      .replace(/\s+/g, "")
      .trim().length;

  const selectedMessage =
    editorMode === "template" ? templateRejectedMessage : customRejectedMessage;
  const selectedMessageLength = hasContent(selectedMessage);
  const isTooLongContent = selectedMessage?.length > 1500;
  const isSendDisabled = !selectedMessageLength || isTooLongContent;
  const isTemplateReadOnly = !isTemplateEditable;

  return (
    <DialogComponent
      open={open}
      onClose={onClose}
      autoWidth
      draggable={false}
      variant="form"
      PaperProps={{
        sx: {
          maxHeight: "calc(100vh - 24px)",
          overflow: "hidden",
        },
      }}
      DialogContentProps={{
        className: "no-drag",
        sx: {
          overflowY: "auto",
          maxHeight: "calc(100vh - 48px)",
        },
      }}
    >
      <Box sx={{ width: { xs: "95vw", sm: "820px" } }}>
        <Typography variant="header" fontSize="28px" fontWeight={700} mb={2}>
          {title}
        </Typography>
        <Box
          sx={{
            border: "1px solid #d5d9e0",
            borderRadius: "6px 6px 0 0",
            overflow: "hidden",
            background: "#eef1f5",
          }}
        >
          <Tabs
            value={editorMode}
            onChange={(_, value: RejectedMessageEditorMode) => {
              setEditorMode(value);
              if (value !== "template") {
                setIsTemplateEditable(false);
              }
            }}
            variant="fullWidth"
          >
            <Tab
              value="template"
              label={t("predefinedRejectedMessageTab")}
              sx={{
                minHeight: "64px",
                textTransform: "none",
                fontSize: "1.05rem",
                fontWeight: 700,
              }}
            />
            <Tab
              value="custom"
              label={t("writeOwnRejectedMessageTab")}
              sx={{
                minHeight: "64px",
                textTransform: "none",
                fontSize: "1.05rem",
                fontWeight: 700,
              }}
            />
          </Tabs>
        </Box>

        <Box sx={{ display: editorMode === "template" ? "block" : "none" }}>
          <RichTextMessageEditor
            value={templateRejectedMessage}
            onChange={setTemplateRejectedMessage}
            readOnly={isTemplateReadOnly}
            showToolbar={!isTemplateReadOnly}
            height={520}
            className="rejected-message-editor"
          />
        </Box>

        <Box sx={{ display: editorMode === "custom" ? "block" : "none" }}>
          <RichTextMessageEditor
            value={customRejectedMessage}
            onChange={setCustomRejectedMessage}
            readOnly={false}
            showToolbar
            height={520}
            className="rejected-message-editor"
          />
        </Box>
        {isTooLongContent && (
          <Box
            sx={{
              borderLeft: "1px solid #d5d9e0",
              borderRight: "1px solid #d5d9e0",
              padding: "8px 16px 0",
              textAlign: "right",
              background: "#f8f9fb",
            }}
          >
            <Typography variant="caption" color={"error"}>
              {t("rejectedMessageMaxLength")}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            border: "1px solid #d5d9e0",
            borderTop: 0,
            borderRadius: "0 0 6px 6px",
            padding: "16px",
            background: "#f8f9fb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: "12px" }}>
            <CustomButton
              variant="outlined"
              sx={{ borderRadius: "18px" }}
              onClick={onClose}
            >
              {t("common:cancel")}
            </CustomButton>
          </Box>
          <Box sx={{ display: "flex", gap: "12px" }}>
            <CustomButton
              variant="outlined"
              sx={{ borderRadius: "18px" }}
              onClick={() => setIsTemplateEditable(true)}
              disabled={editorMode !== "template" || isTemplateEditable}
            >
              {t("common:edit")}
            </CustomButton>
            <CustomButton
              variant="contained"
              sx={{ borderRadius: "18px" }}
              onClick={() => onSend(selectedMessage)}
              disabled={isSendDisabled}
            >
              {t("send", { ns: "common" })}
            </CustomButton>
          </Box>
        </Box>
      </Box>
    </DialogComponent>
  );
};

export default RejectedMessageDialog;
